// 🔧 use_entry.js
// ✅ Purpose: Reusable hook to fetch a single entry/car document from Firestore
// ✅ Features:
// - Fetches car data from /events/{eventId}/cars/{carId}
// - Does NOT use browser caching or localStorage
// - Keeps entry data fresh from Firestore
// - Returns car, loading, and notFound state
// - Fully commented for clarity and debugging

import { useEffect, useState } from "react";
import { doc } from "firebase/firestore";
import { db } from "../firebase";
import { resolveEntrySnapshot } from "../utils/entry_urls";

import { logger } from "../utils/logger";

export const useEntry = (eventId, carId) => {
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // ✅ Reset state when event/car changes
    setCar(null);
    setNotFound(false);

    // ✅ If required IDs are missing, stop loading safely
    if (!eventId || !carId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCar = async () => {
      setLoading(true);

      try {
        const snap = await resolveEntrySnapshot(db, eventId, carId);

        if (cancelled) return;

        if (snap?.exists()) {
          const data = {
            ...snap.data(),
            id: snap.id,
            cid: snap.id,
          };

          setCar(data);

          logger.log("✅ Entry loaded:", {
            eventId,
            carId,
          });
        } else {
          setNotFound(true);

          logger.warn("⚠️ Entry not found:", {
            eventId,
            carId,
          });
        }
      } catch (err) {
        if (cancelled) return;

        logger.error("❌ Failed to fetch entry:", {
          value: err,
        });

        setNotFound(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCar();

    return () => {
      cancelled = true;
    };
  }, [eventId, carId]);

  return {
    car,
    loading,
    notFound,
  };
};
